import axios from "axios";
import config from "../../../utils/config";
import CryptoJS from "crypto-js";
import store from "../../../store/store";
import { isLoggedIn } from "../../../store/reducers/userSlice";
import { Redirect } from "react-router-dom";

// Create an Axios instance
const createAxiosInstance = (baseURL) => {
  const axiosInstance = axios.create({
    baseURL,
  });

  // Request interceptor to add the access token to headers
  axiosInstance.interceptors.request.use(
    (conf) => {
      if (sessionStorage.getItem("access_token")) {
        try {
          const bytes = CryptoJS.AES.decrypt(
            sessionStorage.getItem("access_token"),
            config.tokenKey
          );
          const jsonString = bytes.toString(CryptoJS.enc.Utf8);

          if (jsonString) {
            const token = JSON.parse(jsonString);
            if (token && token !== null) {
              conf.headers["x-access-token"] = token;
              if (
                conf.url.includes("upload_file") ||
                conf.url.includes("excel_validation")
              ) {
                conf.headers["Content-Type"] = "multipart/form-data";
              } else {
                conf.headers["Content-Type"] = "application/json";
                conf.headers["Accept"] = "application/json";
              }
            }
          }
        } catch (error) {
          console.error("Error decrypting or parsing access token:", error);
        }
      }
      return conf;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    failedQueue = [];
  };

  // Response interceptor to handle token expiration
  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      // Check if the error is due to token expiration
      if (error?.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["x-access-token"] = token;
              return axiosInstance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const bytes = CryptoJS.AES.decrypt(
            sessionStorage.getItem("refresh_token"),
            config.tokenKey
          );
          const jsonString = bytes.toString(CryptoJS.enc.Utf8);
          if (jsonString) {
            const refreshToken = JSON.parse(jsonString);
            const session = CryptoJS.AES.decrypt(
              sessionStorage.getItem("session_id"),
              config.tokenKey
            );
            const sessionjsonString = session.toString(CryptoJS.enc.Utf8);
            const session_id = JSON.parse(sessionjsonString);
            if (refreshToken && session_id) {
              const response = await axios.get(
                config.authUrl +
                  "/api/v1/refresh_token?session_id=" +
                  session_id,
                {
                  headers: {
                    "x-access-token": refreshToken,
                    "Content-Type": "application/json",
                  },
                }
              );

              if (response.status === 200) {
                const newAccessToken = response?.data?.data?.access_token;
                const newRefreshToken = response?.data?.data?.refresh_token;
                const encryptedAccessToken = CryptoJS.AES.encrypt(
                  JSON.stringify(newAccessToken),
                  config.tokenKey
                ).toString();
                sessionStorage.setItem("access_token", encryptedAccessToken);
                const encryptedRefreshToken = CryptoJS.AES.encrypt(
                  JSON.stringify(newRefreshToken),
                  config.tokenKey
                ).toString();
                sessionStorage.setItem("refresh_token", encryptedRefreshToken);

                axiosInstance.defaults.headers["x-access-token"] =
                  newAccessToken;
                originalRequest.headers["x-access-token"] = newAccessToken;

                processQueue(null, newAccessToken);

                return axiosInstance(originalRequest);
              }
            }
          }
          processQueue(error, null);
          return Promise.reject(error);
        } catch (refreshError) {
          processQueue(refreshError, null);
          store.dispatch(isLoggedIn(false));
          sessionStorage.clear();
          return <Redirect to="/login" />;
        } finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export const authAxiosInstance = createAxiosInstance(config.authUrl);
export const workflowAxiosInstance = createAxiosInstance(config.workflowUrl);
export const quicklinkAxiosInstance = createAxiosInstance(config.quicklinkUrl);
